import clsx from "clsx";
import { func } from "prop-types";
import {
  createElement,
  forwardRef,
  memo,
  useImperativeHandle,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import { selectDocumentZoom } from "slices/documentViewer/selectors";

import {
  DOCX_PREVIEW_ID,
  DOCX_PREVIEW_STYLES_ID,
  FILE_PROP_TYPE,
} from "./constants";
import DocXContentNode from "./DocXContentNode";
import classes from "./DocXDocument.module.scss";
import { useSetZoomByContainer } from "./hooks";
import useDocxDocument from "./hooks/useDocxDocument";
import { getCommonPageVars } from "./utils/styles";

const DocXDocument = forwardRef(({ file, onLoadSuccess }, forwardedRef) => {
  const zoom = useSelector(selectDocumentZoom);
  const containerRef = useRef(null);
  const pagesRef = useRef(null);

  useSetZoomByContainer(containerRef);
  const docx = useDocxDocument(file, pagesRef, {
    onLoadSuccess,
  });

  const { hasSinglePage, isLoading, nodes, scrollToCenter, virtualizer } = docx;
  const items = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();
  const translateY = items[0]?.start ?? 0;

  useImperativeHandle(forwardedRef, () => {
    return {
      scrollToCenter,
      virtualizer,
    };
  });

  return (
    <div
      className={clsx(classes["docx_document"])}
      ref={containerRef}
      style={getCommonPageVars(zoom)}
    >
      {isLoading && <p>Loading File ...</p>}
      <div className={clsx(classes["docx_document__wrapper"])} ref={pagesRef}>
        <div
          className={clsx(
            classes["docx_document__virtual_list"],
            DOCX_PREVIEW_ID
          )}
          style={{ height: `${totalHeight}px` }}
        >
          {createElement(
            hasSinglePage ? "section" : "div",
            {
              className: classes["docx_document__virtual_list__start"],
              style: { transform: `translateY(${translateY}px)` },
            },
            items.map((virtualItem) => {
              const nodeData = nodes[virtualItem.index];

              return (
                <DocXContentNode
                  hasSinglePage={hasSinglePage}
                  index={virtualItem.index}
                  key={virtualItem.key}
                  data={nodeData}
                  ref={virtualizer.measureElement}
                />
              );
            })
          )}
        </div>
      </div>

      <div id={DOCX_PREVIEW_STYLES_ID} />
    </div>
  );
});

DocXDocument.displayName = "DocXDocument";

DocXDocument.propTypes = {
  file: FILE_PROP_TYPE,
  onLoadSuccess: func,
};

export default memo(DocXDocument, (prev, next) => {
  const prevFile = prev.file;
  const nextFile = next.file;

  return Object.is(prevFile, nextFile);
});
